import { ChipGroup, formatDatum } from '@elbklar/ui';
import { TextField } from '@/components/form/Felder';

/*
 * Wiedervorlage an einer Interaktion (Henri, 26.10.2026). Gespeichert wird
 * `follow_up_date`; die Aufgabe „Nachfassen: <Betreff>“ legt das Backend in
 * derselben Transaktion an, verschiebt oder bricht sie ab.
 *
 * Bedienung: eine Schnellauswahl als Chips, „Keine“ vorgewählt. Am Handy ist
 * das ein Tippen; eine Checkbox mit Auswahl darunter wären zwei bis drei
 * (Checkbox, Auswahl öffnen, wählen), ein Datumsfeld öffnet den Kalender
 * des Systems, in dem man für „in 2 Wochen“ erst zählt. Im vollen Formular
 * steht unter den Chips zusätzlich das Datum, zum Ändern auf einen genauen
 * Tag („Rückruf am Donnerstag“) und beim Bearbeiten für das gespeicherte
 * Datum, das zu keinem Chip passt.
 */

/** Gewählt: `null` heißt keine Wiedervorlage, sonst der Kalendertag `YYYY-MM-DD` ('' solange das Datum fehlt). */
export type Wiedervorlage = string | null;

const WAHL = [
  { value: 'keine', label: 'Keine' },
  { value: 't1', label: 'Morgen' },
  { value: 't7', label: '1 Woche' },
  { value: 't14', label: '2 Wochen' },
  { value: 'm1', label: '1 Monat' },
  { value: 'm3', label: '3 Monate' },
];

const zwei = (n: number) => `${n}`.padStart(2, '0');
const tagText = (d: Date) => `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;

/** Der Tag zu einem Chip, gerechnet ab heute in Ortszeit. Monate ohne Überlauf: 31.01. plus 1 Monat ist der 28.02. */
function tagFuer(wahl: string, heute = new Date()): string {
  const art = wahl[0];
  const n = Number(wahl.slice(1));
  if (art === 't') return tagText(new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + n));
  const letzter = new Date(heute.getFullYear(), heute.getMonth() + n + 1, 0).getDate();
  return tagText(new Date(heute.getFullYear(), heute.getMonth() + n, Math.min(heute.getDate(), letzter)));
}

/** `follow_up_date` aus der API (Zeitstempel) als Kalendertag in Ortszeit. */
export function wiedervorlageAus(followUpDate: string | null | undefined): Wiedervorlage {
  if (!followUpDate) return null;
  const d = new Date(followUpDate);
  return Number.isNaN(d.getTime()) ? null : tagText(d);
}

/**
 * Kalendertag als `follow_up_date`: Mittag Ortszeit. Das Backend nimmt den
 * Kalendertag in Europe/Berlin; Mittag bleibt für jede Zeitzone im Abstand
 * von elf Stunden derselbe Tag.
 */
export function followUpDateAus(tag: string): string {
  const [j, m, t] = tag.split('-').map(Number);
  return new Date(j, m - 1, t, 12).toISOString();
}

interface WiedervorlageFeldProps {
  value: Wiedervorlage;
  onChange: (wert: Wiedervorlage) => void;
  /** Kurzform (Schnell erfassen): nur die Chips, das Datum als Satz darunter. */
  kompakt?: boolean;
  error?: string;
}

export function WiedervorlageFeld({ value, onChange, kompakt = false, error }: WiedervorlageFeldProps) {
  const gewaehlt = value === null ? 'keine' : (WAHL.find((w) => w.value !== 'keine' && tagFuer(w.value) === value)?.value ?? '');

  return (
    <div className="flex flex-col gap-2">
      <span className="t-body-strong">Wiedervorlage</span>
      <ChipGroup label="Wiedervorlage" items={WAHL} value={gewaehlt} onChange={(w) => onChange(w === 'keine' ? null : tagFuer(w))} />
      {value !== null &&
        (kompakt ? (
          <p className="t-small text-muted-foreground">Erinnert am {formatDatum(value)} als Aufgabe „Nachfassen“.</p>
        ) : (
          <TextField
            label="Wiedervorlage am"
            type="date"
            required
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            hint="Erinnert als Aufgabe „Nachfassen“ an diesem Tag."
            className="mt-2"
          />
        ))}
    </div>
  );
}
